import {Instance} from 'flatpickr/dist/types/instance';
import {Plugin} from 'flatpickr/dist/types/options';

export namespace ShortcutButtonsFlatpickr {
    export type OnClickSignature = (index: number, fp: Instance) => void;

    export type Attributes = { [name: string]: string };

    export type Button = {
        attributes?: Attributes,
        label: string,
        className: string
    };

    export type Config = {
        button: Button | Button[],
        label?: string,
        onClick?: OnClickSignature | OnClickSignature[],
        theme?: string,
    };
}

export function ShortcutButtonsPlugin(config: ShortcutButtonsFlatpickr.Config): Plugin {
    const cfg = {...config};

    return (fp) => {
        /**
         * Element that wraps this plugin's dependent elements.
         */
        let buttons: HTMLElement | undefined;

        /**
         * Handles click events on plugin's button.
         */
        function onClick(event: Event) {
            event.stopPropagation();
            event.preventDefault();

            const target = event.target as HTMLButtonElement;
            if (target.tagName.toLowerCase() !== 'button' || typeof cfg.onClick === 'undefined') {
                return;
            }

            if (!target.dataset.index) {
                return;
            }

            const index = parseInt(target.dataset.index, 10);

            const callbacks = Array.isArray(cfg.onClick) ?
                cfg.onClick :
                [cfg.onClick];

            for (const callback of callbacks) {
                if (typeof callback === 'function') {
                    callback(index, fp);
                }
            }
        }

        /**
         * Handles key down events on plugin's button.
         */
        function onKeyDown(event: KeyboardEvent) {
            const target = event.target as HTMLButtonElement;
            if (event.key !== 'Tab' || target.tagName.toLowerCase() !== 'button') {
                return;
            }

            if ((event.shiftKey && !target.previousSibling) || (!event.shiftKey && !target.nextSibling)) {
                event.preventDefault();
                fp.element.focus();
            }
        }

        return {
            onReady: () => {

                buttons = document.createElement('div');
                buttons.classList.add('dfb-Datepicker-buttonContainer');

                (Array.isArray(cfg.button) ? cfg.button : [cfg.button]).forEach((b, index) => {
                    const button = document.createElement('button');
                    button.type = 'button';
                    button.classList.add('dfb-Datepicker-button');
                    button.textContent = b.label;
                    button.dataset.index = String(index);

                    if (b.className) {
                        button.classList.add(b.className);
                    }

                    if (buttons) {
                        buttons.appendChild(button);
                    }

                    fp.pluginElements.push(button);
                });

                fp.calendarContainer.appendChild(buttons);

                buttons.addEventListener('click', onClick);
                buttons.addEventListener('keydown', onKeyDown);
            },

            /**
             * Clean up before flatpickr is destroyed.
             */
            onDestroy: () => {
                if (!buttons) {
                    return;
                }

                buttons.removeEventListener('keydown', onKeyDown);
                buttons.removeEventListener('click', onClick);
            }
        };
    };
}
